import { useMutation, useQuery } from '@apollo/client/react';
import { OctagonAlertIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import Header from '../../Header';
import { RightSidebarWrapper } from '../../RightSidebar/RightSidebarWrapper';
import {
  formatAllocationType,
  formatCurrency,
  formatPercentage,
  formatStatus,
  GET_ALLOCATION_DETAILS,
  GET_ALLOCATION_HISTORY,
  type GetAllocationDetailsResponse,
  getStatusBadgeClass,
  SUBMIT_FOR_REVIEW,
  type SubmitForReviewResponse
} from '../graphql';

import { EditButton } from '@/components/EditButton';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="grid grid-cols-2">
    <div className="text-sm">{label}</div>
    <TruncatedTextTooltip className="text-muted-foreground font-medium">
      {value}
    </TruncatedTextTooltip>
  </div>
);

const InvestmentsDirectIdAllocationDetails = () => {
  const { allocationId, id: dealId } = useParams<{
    allocationId: string;
    id: string;
  }>();

  if (!allocationId) {
    throw new Error('Missing allocation ID');
  }

  const decodedDealId = dealId ? atob(dealId).split(':')[1] : null;

  const { data, error, loading, refetch } = useQuery<
    GetAllocationDetailsResponse,
    { id: string }
  >(GET_ALLOCATION_DETAILS, {
    skip: !allocationId,
    variables: { id: allocationId }
  });

  const [submitForReview, { loading: isSubmittingForReview }] = useMutation<
    SubmitForReviewResponse,
    { allocationId: string }
  >(SUBMIT_FOR_REVIEW);

  const [activeTab, setActiveTab] = useState('info');
  const allocation = data?.node;

  if (loading) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center text-red-500">
        Error loading allocation: {error.message}
      </div>
    );
  }

  if (!allocation) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center text-red-500">
        Allocation not found
      </div>
    );
  }

  const handleSubmitForReview = async () => {
    try {
      const { data: result } = await submitForReview({
        refetchQueries: decodedDealId
          ? [
              {
                query: GET_ALLOCATION_HISTORY,
                variables: { opportunityId: decodedDealId }
              }
            ]
          : [],
        variables: { allocationId: atob(allocationId).split(':')[1] }
      });

      if (result?.submitForReview.ok) {
        toast.success('Submitted for Review', {
          description:
            'The allocation has been successfully submitted for review'
        });
        void refetch();
      } else {
        toast.error(
          result?.submitForReview.errors?.[0] || 'Failed to submit for review'
        );
      }
    } catch {
      toast.error('Failed to submit for review');
    }
  };

  const allocationLines = allocation.allocationLines ?? [];
  const warnings = allocation.warnings ?? [];
  const canSubmitForReview = allocation.status?.toLowerCase() === 'draft';

  return (
    <>
      <Header>
        {canSubmitForReview && (
          <Button
            disabled={isSubmittingForReview}
            onClick={() => void handleSubmitForReview()}
          >
            {isSubmittingForReview && <Spinner />}
            Submit for Review
          </Button>
        )}
      </Header>
      <RightSidebarWrapper>
        <div className="flex min-w-0 flex-col gap-2.5 px-5 pt-5 pb-16.5 md:pl-14">
          <Tabs
            className="gap-2.5"
            onValueChange={setActiveTab}
            value={activeTab}
          >
            <TabsList className="w-full">
              <TabsTrigger
                className="flex-1"
                value="info"
              >
                Allocation Info
              </TabsTrigger>
              <TabsTrigger
                className="flex-1"
                value="lines"
              >
                Allocation Lines
              </TabsTrigger>
              <TabsTrigger
                className="flex-1"
                value="overview"
              >
                Overview
              </TabsTrigger>
            </TabsList>

            <TabsContent value="info">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">Allocation Info</div>
                  <EditButton className="opacity-0" />
                </div>
                <div className="bg-card rounded-md border p-5">
                  {!allocation.isFulfilled && (
                    <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-600">
                      <OctagonAlertIcon className="size-4" />
                      <span className="font-medium">Not Fulfilled</span>
                    </div>
                  )}
                  {allocation.isFulfilled && (
                    <div className="flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-600">
                      <span className="font-medium">Fulfilled</span>
                    </div>
                  )}
                  <div className="@container mt-5 grid gap-4">
                    <div className="grid gap-5 @md:grid-cols-2">
                      <InfoRow
                        label="Number of Funds"
                        value={allocation.numberOfFunds ?? '-'}
                      />
                      <div className="grid grid-cols-2">
                        <div className="text-sm">Allocation Type</div>
                        <Badge className="bg-fuchsia-200 whitespace-normal text-fuchsia-800">
                          {formatAllocationType(allocation.allocationType)}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid gap-5 @md:grid-cols-2">
                      <InfoRow
                        label="Requested Amount"
                        value={formatCurrency(allocation.allocationAmount)}
                      />
                      <InfoRow
                        label="Use of Proceeds"
                        value={
                          allocation.useOfProceeds
                            ?.split('_')
                            .map(
                              word =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(' ') ?? '-'
                        }
                      />
                    </div>
                    <div className="grid gap-5 @md:grid-cols-2">
                      <InfoRow
                        label="Allocated Amount"
                        value={formatCurrency(allocation.totalAllocated)}
                      />
                      <div className="grid grid-cols-2">
                        <div className="text-sm">Status</div>
                        <Badge
                          className={cn(
                            'whitespace-normal',
                            getStatusBadgeClass(allocation.status)
                          )}
                        >
                          {formatStatus(allocation.status)}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid gap-5 @md:grid-cols-2">
                      <InfoRow
                        label="Debt Allocation"
                        value={formatCurrency(allocation.debtAllocationAmount)}
                      />
                      <InfoRow
                        label="Equity Allocation"
                        value={formatCurrency(
                          allocation.equityAllocationAmount
                        )}
                      />
                    </div>
                    <div className="grid gap-5 @md:grid-cols-2">
                      <InfoRow
                        label="Version"
                        value={allocation.version ?? '-'}
                      />
                      <InfoRow
                        label="FinOps Reviewed"
                        value={allocation.finopsReviewed ? 'Yes' : 'No'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="lines">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">Allocation Lines</div>
                  <EditButton className="opacity-0" />
                </div>
                <div className="bg-card rounded-md border p-5">
                  {allocationLines.length === 0 ? (
                    <div className="text-muted-foreground py-8 text-center">
                      No allocation lines found.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-semibold">
                            Fund
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Allocated Amount
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Allocation %
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Fair Share Amount
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Concentration Limit %
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Concentration Limit Amount
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Over Limit
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Excluded
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Balance
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allocationLines.map(line => (
                          <TableRow key={line.id}>
                            <TableCell>{line.fund?.name ?? '-'}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(line.allocatedAmount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatPercentage(line.allocationPercentage)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(line.fairShareAmount)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatPercentage(line.concentrationLimitPct)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(line.concentrationLimitAmount)}
                            </TableCell>
                            <TableCell className="text-center">
                              {line.isOverLimit ? (
                                <Badge className="bg-red-200 text-red-800">
                                  Yes
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">
                                  No
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {line.isExcluded ? (
                                <Badge className="bg-gray-200 text-gray-800">
                                  Yes
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">
                                  No
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(line.fund?.balance)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="overview">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-semibold">
                    Overview (Warnings)
                  </div>
                  <EditButton className="opacity-0" />
                </div>
                <div className="bg-card rounded-md border p-5">
                  {warnings.length === 0 ? (
                    <div className="text-muted-foreground py-8 text-center">
                      No warnings for this allocation.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-semibold">
                            Fund
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Warning Type
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Message
                          </TableHead>
                          <TableHead className="text-xs font-semibold">
                            Severity
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {warnings.map(warning => (
                          <TableRow key={warning.id}>
                            <TableCell>
                              {warning.fund?.name ?? 'General'}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-orange-200 text-orange-800">
                                {warning.warningType
                                  ?.split('_')
                                  .map(
                                    word =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(' ') ?? '-'}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-md truncate">
                              {warning.message ?? '-'}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  warning.severity === 'error'
                                    ? 'bg-red-200 text-red-800'
                                    : warning.severity === 'warning'
                                      ? 'bg-orange-200 text-orange-800'
                                      : 'bg-blue-200 text-blue-800'
                                }
                              >
                                {warning.severity
                                  ? warning.severity.charAt(0).toUpperCase() +
                                    warning.severity.slice(1)
                                  : '-'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdAllocationDetails;
